import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import client from '../api/client';
import { 
    Briefcase, Plus, User, Check, X, Edit2, Trash2, 
    ShieldAlert, Database, Search, Filter, Download, 
    Globe, Server, Key, Link as LinkIcon, Shield, 
    ExternalLink, Calendar, ChevronRight, Phone, Send,
    Bell, BellOff, MessageCircle, MapPin, Zap, TrendingUp, Users as UsersIcon
} from 'lucide-react';
import ClientAssetsModal from '../components/ClientAssetsModal';
import { useAuth } from '../context/AuthContext';
import { useDevice } from '../hooks/useDevice';

const ClientCard = ({ client: c, isManager, onEdit, onDelete, onAssets }) => (
    <div className="bg-neutral-900 border border-neutral-800 rounded-[2.5rem] md:rounded-[3rem] p-1 shadow-2xl hover:border-blue-500/50 transition-all group overflow-hidden flex flex-col min-h-[400px] md:min-h-[450px] italic">
        <div className="p-6 md:p-8 pb-4">
            <div className="flex items-start justify-between mb-6 md:mb-8">
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-[1.5rem] md:rounded-[2rem] bg-neutral-950 border border-neutral-800 flex items-center justify-center overflow-hidden shadow-inner group-hover:scale-105 transition-all">
                    {c.photo_url ? <img src={c.photo_url} className="w-full h-full object-cover" /> : <User className="w-8 h-8 text-neutral-700" />}
                </div>
                <div className="flex flex-col items-end gap-2 text-[8px] md:text-[9px] font-black text-neutral-600 uppercase tracking-widest">
                    <span className="bg-neutral-950 px-3 py-1.5 rounded-full border border-neutral-800">ID: {c.id}</span>
                    {c.direction && <span className="bg-blue-500/5 text-blue-500 px-3 py-1.5 rounded-full border border-blue-500/10">{c.direction}</span>}
                </div>
            </div>
            <h3 className="text-white font-black text-xl md:text-2xl tracking-tighter leading-none mb-1 uppercase truncate">{c.full_name || c.name}</h3>
            <p className="text-[10px] text-neutral-500 font-bold uppercase tracking-widest mb-6 md:mb-8 truncate">{c.company_name || 'Частное лицо'}</p>
            
            <div className="grid grid-cols-2 gap-3 md:gap-4">
                <div className="bg-neutral-950/40 p-4 md:p-5 rounded-[1.5rem] md:rounded-3xl border border-neutral-800/40">
                    <p className="text-[7px] md:text-[8px] font-black text-neutral-600 uppercase tracking-widest mb-1 italic">Сфера</p>
                    <p className="text-[10px] md:text-xs text-white font-bold truncate">{c.direction || '—'}</p>
                </div>
                <div className="bg-neutral-950/40 p-4 md:p-5 rounded-[1.5rem] md:rounded-3xl border border-neutral-800/40">
                    <p className="text-[7px] md:text-[8px] font-black text-neutral-600 uppercase tracking-widest mb-1 italic">Результат</p>
                    <p className="text-[10px] md:text-xs text-emerald-400 font-black">{c.turnover || '—'}</p>
                </div>
            </div>
        </div>
        <div className="mt-auto p-6 md:p-8 pt-0">
            <div className="flex items-center gap-3 pt-4 md:pt-6 border-t border-neutral-800/50">
                <button onClick={() => onEdit(c)} className="flex-1 bg-neutral-800 hover:bg-neutral-700 text-white text-[9px] font-black uppercase tracking-widest py-3.5 md:py-4 rounded-xl md:rounded-2xl transition-all italic">Профиль</button>
                <button onClick={() => onAssets(c)} className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-blue-500/5 text-blue-500 rounded-xl md:rounded-2xl hover:bg-blue-600 hover:text-white transition-all border border-blue-500/10"><Database size={20} /></button>
                {isManager && <button onClick={() => onDelete(c.id)} className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-rose-500/5 text-rose-500 rounded-xl md:rounded-2xl hover:bg-rose-500 hover:text-white transition-all border border-rose-500/10"><Trash2 size={20} /></button>}
            </div>
        </div>
    </div>
);

const Clients = () => {
    const { user } = useAuth();
    const { isMobile } = useDevice();
    const queryClient = useQueryClient();
    
    // UI State
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState('clients'); // 'clients' or 'assets'
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingClient, setEditingClient] = useState(null);
    const [isAssetsModalOpen, setIsAssetsModalOpen] = useState(false);
    const [selectedClientForAssets, setSelectedClientForAssets] = useState(null);
    const [assetSearch, setAssetSearch] = useState('');
    const [assetTypeFilter, setAssetTypeFilter] = useState('all');

    const [formData, setFormData] = useState({
        full_name: '', dob: '', direction: '', experience_years: '', turnover: '',
        team_size: '', origin_city: '', business_geography: '', company_name: '',
        referral_source: '', marital_status: '', wives_count: 0, children_count: 0,
        hobbies: '', dream: '', sport_activity: '', request_text: '',
        preferred_messenger: 'Telegram', phone: '', landline_phone: '',
        email: '', telegram_chat_id: '', passport_photo_url: '', photo_url: '',
        notify_payment: false, notify_progress: false
    });

    const isManager = ['admin', 'superadmin', 'rop'].includes(user?.role);
    const canManage = ['admin', 'superadmin', 'rop', 'partner', 'seller', 'employee', 'staff'].includes(user?.role);

    // Queries
    const { data: clients, isLoading } = useQuery({
        queryKey: ['clients'],
        queryFn: async () => (await client.get('/clients')).data,
    });

    const { data: allAssets, isLoading: isLoadingAssets } = useQuery({
        queryKey: ['all-assets'],
        queryFn: async () => (await client.get('/client-assets/all')).data,
        enabled: activeTab === 'assets'
    });

    // Mutations
    const mutation = useMutation({
        mutationFn: (data) => editingClient ? client.patch(`/clients/${editingClient.id}`, data) : client.post('/clients', data),
        onSuccess: () => { queryClient.invalidateQueries(['clients']); closeModal(); }
    });

    const deleteMutation = useMutation({
        mutationFn: (id) => client.delete(`/clients/${id}`),
        onSuccess: () => queryClient.invalidateQueries(['clients'])
    });

    const handleEdit = (c) => {
        setEditingClient(c);
        setFormData({ ...c, dob: c.dob ? c.dob.split('T')[0] : '' });
        setIsModalOpen(true);
    };

    const handleDelete = (id) => {
        if (window.confirm("Удалить клиента из базы данных?")) {
            deleteMutation.mutate(id);
        }
    };

    const closeModal = () => {
        setIsModalOpen(false); setEditingClient(null);
        setFormData({
            full_name: '', dob: '', direction: '', experience_years: '', turnover: '',
            team_size: '', origin_city: '', business_geography: '', company_name: '',
            referral_source: '', marital_status: '', wives_count: 0, children_count: 0,
            hobbies: '', dream: '', sport_activity: '', request_text: '',
            preferred_messenger: 'Telegram', phone: '', landline_phone: '',
            email: '', telegram_chat_id: '', passport_photo_url: '', photo_url: '',
            notify_payment: false, notify_progress: false
        });
    };

    const filteredClients = useMemo(() => {
        if (!Array.isArray(clients)) return [];
        return clients.filter(c => 
            (c.full_name || c.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (c.company_name || '').toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [clients, searchTerm]);

    if (!canManage) return (
        <div className="flex flex-col items-center justify-center p-20 text-neutral-500 italic">
            <ShieldAlert className="w-16 h-16 mb-4 text-rose-500/50" />
            <h2 className="text-xl font-bold text-white uppercase tracking-widest mb-2">Доступ закрыт</h2>
            <p className="text-[10px] uppercase font-black">Протокол безопасности CRM</p>
        </div>
    );

    // --- RESPONSIVE UI ---
    return (
        <div className="max-w-7xl mx-auto space-y-8 md:space-y-12 animate-in fade-in duration-700 pb-24 md:pb-32 px-4 italic">
            {/* Unified Header */}
            <header className="flex flex-col md:flex-row md:justify-between md:items-end gap-6 pt-4 md:pt-8">
                <div>
                    <h1 className="text-3xl md:text-5xl font-black text-white italic uppercase tracking-tighter leading-none">CRM / Резиденты</h1>
                    <p className="text-neutral-500 text-[10px] md:text-xs font-black uppercase tracking-[0.2em] md:tracking-[0.4em] mt-2 md:mt-3 italic">Million Connections Core Database</p>
                </div>
                <div className="flex flex-col md:flex-row items-center gap-4 w-full md:w-auto">
                    <div className="bg-neutral-900 p-1.5 rounded-2xl md:rounded-[2.5rem] border border-neutral-800 shadow-xl flex w-full md:w-fit">
                        <button onClick={() => setActiveTab('clients')} className={`flex-1 md:flex-none px-6 md:px-10 py-3 md:py-4 rounded-xl md:rounded-[2rem] font-black uppercase text-[10px] tracking-widest transition-all ${activeTab === 'clients' ? 'bg-blue-600 text-white shadow-xl' : 'text-neutral-500'}`}>База</button>
                        <button onClick={() => setActiveTab('assets')} className={`flex-1 md:flex-none px-6 md:px-10 py-3 md:py-4 rounded-xl md:rounded-[2rem] font-black uppercase text-[10px] tracking-widest transition-all ${activeTab === 'assets' ? 'bg-blue-600 text-white shadow-xl' : 'text-neutral-500'}`}>Знания</button>
                    </div>
                    {activeTab === 'clients' && (
                        <button onClick={() => setIsModalOpen(true)} className="w-full md:w-auto bg-white text-black px-8 py-5 rounded-2xl md:rounded-[2rem] font-black uppercase text-[10px] tracking-widest hover:bg-neutral-200 transition-all shadow-xl flex items-center justify-center gap-3 italic">
                            <Plus size={20}/> Добавить
                        </button>
                    )}
                </div>
            </header>

            {/* Search & Filter Unified */}
            <div className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-1 group">
                    <Search className="absolute left-6 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-600 transition-colors group-focus-within:text-blue-500" />
                    <input 
                        type="text" 
                        placeholder={activeTab === 'clients' ? "Поиск по резидентам..." : "Поиск в базе знаний..."} 
                        value={activeTab === 'clients' ? searchTerm : assetSearch} 
                        onChange={(e) => activeTab === 'clients' ? setSearchTerm(e.target.value) : setAssetSearch(e.target.value)} 
                        className="w-full bg-neutral-900 border border-neutral-800 text-white pl-16 pr-6 py-5 md:py-6 rounded-[2rem] text-sm font-bold outline-none focus:border-blue-500 shadow-2xl transition-all" 
                    />
                </div>
                {activeTab === 'assets' && (
                    <div className="bg-neutral-900 border border-neutral-800 rounded-[2rem] px-6 py-4 flex items-center gap-3 shadow-xl">
                        <Filter size={16} className="text-blue-500" />
                        <select 
                            value={assetTypeFilter}
                            onChange={(e) => setAssetTypeFilter(e.target.value)}
                            className="bg-transparent text-white text-[10px] font-black uppercase tracking-widest outline-none cursor-pointer"
                        >
                            <option value="all">Все категории</option>
                            <option value="access">🔐 Доступы</option>
                            <option value="domain">🌐 Домены</option>
                            <option value="hosting">🖥 Хостинг</option>
                            <option value="link">🔗 Ссылки</option>
                        </select>
                    </div>
                )}
            </div>

            {/* Main Content Area */}
            <div className="relative min-h-[400px]">
                {isLoading || (activeTab === 'assets' && isLoadingAssets) ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="h-[450px] bg-neutral-900 rounded-[3rem] border border-neutral-800 animate-pulse" />)}
                    </div>
                ) : activeTab === 'clients' ? (
                    filteredClients.length === 0 ? (
                        <div className="py-20 text-center opacity-20 flex flex-col items-center gap-4">
                            <UsersIcon size={48} />
                            <p className="text-xs font-black uppercase tracking-widest">Клиенты не найдены</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6 md:gap-8">
                            {filteredClients.map(c => (
                                <ClientCard 
                                    key={c.id} 
                                    client={c} 
                                    isManager={isManager} 
                                    onEdit={handleEdit} 
                                    onDelete={handleDelete} 
                                    onAssets={(client) => { setSelectedClientForAssets(client); setIsAssetsModalOpen(true); }}
                                />
                            ))}
                        </div>
                    )
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {allAssets?.filter(asset => {
                            const matchesSearch = asset.name.toLowerCase().includes(assetSearch.toLowerCase()) || 
                                                 asset.client_name.toLowerCase().includes(assetSearch.toLowerCase());
                            const matchesType = assetTypeFilter === 'all' || asset.type === assetTypeFilter;
                            return matchesSearch && matchesType;
                        }).map(asset => (
                            <div key={asset.id} className="bg-neutral-900 border border-neutral-800 p-8 rounded-[3rem] hover:border-blue-500/30 transition-all group relative overflow-hidden italic shadow-2xl">
                                <div className="flex justify-between items-start mb-8 relative">
                                    <div className="flex items-center gap-5">
                                        <div className="w-14 h-14 rounded-2xl bg-neutral-950 border border-neutral-800 flex items-center justify-center shadow-inner group-hover:scale-105 transition-transform">
                                            {asset.type === 'access' && <Key className="w-6 h-6 text-amber-500" />}
                                            {asset.type === 'domain' && <Globe className="w-6 h-6 text-sky-500" />}
                                            {asset.type === 'hosting' && <Server className="w-6 h-6 text-emerald-500" />}
                                            {asset.type === 'link' && <LinkIcon className="w-6 h-6 text-pink-500" />}
                                            {asset.type === 'other' && <Shield className="w-6 h-6 text-neutral-500" />}
                                        </div>
                                        <div>
                                            <h4 className="text-white font-black text-xl italic uppercase tracking-tighter leading-tight truncate max-w-[150px]">{asset.name}</h4>
                                            <p className="text-[9px] text-blue-500 uppercase font-black tracking-widest mt-2">{asset.client_name}</p>
                                        </div>
                                    </div>
                                    <button 
                                        onClick={() => { setSelectedClientForAssets({ id: asset.client_id, name: asset.client_name }); setIsAssetsModalOpen(true); }}
                                        className="w-12 h-12 flex items-center justify-center bg-white/5 hover:bg-white/10 text-neutral-400 hover:text-white rounded-xl transition-all"
                                    >
                                        <Edit2 size={18} />
                                    </button>
                                </div>
                                <div className="space-y-4">
                                    {asset.data?.url && (
                                        <a href={asset.data.url} target="_blank" rel="noreferrer" className="text-sm text-blue-400 font-bold flex items-center gap-2 hover:underline">
                                            <ExternalLink size={14}/> {asset.data.url.replace(/^https?:\/\//, '')}
                                        </a>
                                    )}
                                    <div className="mt-6 pt-6 border-t border-neutral-800/50 flex items-center justify-between">
                                        <span className="text-[8px] font-black text-neutral-600 uppercase tracking-widest">Срок хранения</span>
                                        <span className="text-[10px] font-black text-neutral-400 italic">22.04.2025</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Modals & Popups */}
            <ClientAssetsModal 
                isOpen={isAssetsModalOpen} 
                onClose={() => setIsAssetsModalOpen(false)} 
                client_id={selectedClientForAssets?.id} 
                client_name={selectedClientForAssets?.name || selectedClientForAssets?.full_name} 
            />

            {isModalOpen && (
                <div className="fixed inset-0 bg-black/95 backdrop-blur-3xl z-[200] flex items-center justify-center p-4 animate-in zoom-in-95 duration-300">
                    <div className="bg-neutral-900 border border-neutral-800 rounded-[3rem] md:rounded-[4rem] w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col italic">
                        <div className="p-8 md:p-12 pb-6 md:pb-8 flex justify-between items-center border-b border-white/5">
                            <div>
                                <h2 className="text-2xl md:text-3xl font-black text-white uppercase italic tracking-tighter leading-none">
                                    {editingClient ? 'Правка Досье' : 'Новое Поступление'}
                                </h2>
                                <p className="text-[10px] text-neutral-500 font-bold uppercase tracking-widest mt-2 italic md:block hidden">CRM Record Protection Activated</p>
                            </div>
                            <button onClick={closeModal} className="w-12 h-12 md:w-16 md:h-16 bg-neutral-800 rounded-2xl flex items-center justify-center hover:bg-white hover:text-black transition-all">
                                <X size={24}/>
                            </button>
                        </div>
                        <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(formData); }} className="p-8 md:p-12 pt-8 overflow-y-auto custom-scrollbar flex-1 space-y-8 md:space-y-10 text-left">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                                <div className="col-span-full">
                                    <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest px-2 mb-2 block italic">Полное имя резидента</label>
                                    <input required type="text" value={formData.full_name} onChange={e => setFormData({ ...formData, full_name: e.target.value })} className="w-full bg-neutral-950 border border-neutral-800 rounded-[1.5rem] md:rounded-[2rem] px-6 py-4.5 md:py-5 text-white font-black md:text-xl outline-none focus:border-blue-500 transition-all" />
                                </div>
                                <div>
                                    <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest px-2 mb-2 block italic">Компания / Бренд</label>
                                    <input type="text" value={formData.company_name} onChange={e => setFormData({ ...formData, company_name: e.target.value })} className="w-full bg-neutral-950 border border-neutral-800 rounded-2xl px-6 py-4 text-white font-bold outline-none focus:border-blue-500 transition-all" />
                                </div>
                                <div>
                                    <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest px-2 mb-2 block italic">Индустрия</label>
                                    <input type="text" value={formData.direction} onChange={e => setFormData({ ...formData, direction: e.target.value })} className="w-full bg-neutral-950 border border-neutral-800 rounded-2xl px-6 py-4 text-white font-bold outline-none focus:border-blue-500 transition-all" />
                                </div>
                                <div>
                                    <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest px-2 mb-2 block italic">Оборот (Средний)</label>
                                    <input type="text" value={formData.turnover} onChange={e => setFormData({ ...formData, turnover: e.target.value })} className="w-full bg-neutral-950 border border-neutral-800 rounded-2xl px-6 py-4 text-emerald-400 font-black outline-none focus:border-blue-500 transition-all" />
                                </div>
                                <div>
                                    <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest px-2 mb-2 block italic">Контакт (Phone/TG)</label>
                                    <input type="text" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} className="w-full bg-neutral-900 border border-neutral-800 rounded-2xl px-6 py-4 text-white font-bold outline-none focus:border-blue-500 transition-all" />
                                </div>
                            </div>
                            <button type="submit" disabled={mutation.isPending} className="w-full bg-blue-600 text-white py-6 md:py-8 rounded-[2rem] md:rounded-[2.5rem] font-black uppercase text-[10px] md:text-sm tracking-[0.2em] shadow-2xl shadow-blue-500/20 active:scale-95 transition-all italic">
                                {mutation.isPending ? 'Запись в базу...' : 'Зафиксировать резидента'}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Clients;
